import { useEffect, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useMatches } from 'react-router-dom';

import { useLanguageOptions } from '@/components/LanguageSelector/hooks';
import { TRIAL_DAYS } from '@/config';
import { DEFAULT_LANGUAGE } from '@/i18next';
import { getAbsolutePath, stripTrailingSlash } from '@/utils/locale';

import { getCanonicalPath } from './utils';

function SeoTags() {
	const location = useLocation();
	const langOptions = useLanguageOptions();
	const pageNotFound = useSelector(state => state.site.pageNotFound);
	const { t, i18n } = useTranslation();
	const matches = useMatches();

	const { origin } = window.location;

	const absolutePath = getAbsolutePath(location?.pathname);
	const defaultRoute = stripTrailingSlash(`${origin}${absolutePath}`);

	const description = useMemo(() => {
		let newDescription = t('seo.description');

		for (const match of matches) {
			if (!match.id) {
				continue;
			}

			const seoLocaleKey = `seo.${match.id}.description`;
			if (i18n.exists(seoLocaleKey)) {
				newDescription = t(seoLocaleKey, { trialDays: TRIAL_DAYS });
				break;
			}
		}
		return newDescription;
	}, [matches, i18n.language, i18n.isInitialized]);

	useEffect(() => {
		const meta = document.querySelectorAll('meta[data-initial]');
		meta.forEach(m => m.remove());
	}, []);

	if (pageNotFound) {
		return null;
	}

	return (
		<>
			<meta name="description" content={description} />
			<meta property="og:description" content={description} />
			<meta name="twitter:description" content={description} />

			<link
				rel="canonical"
				href={`${origin}${getCanonicalPath(location.pathname)}`}
			/>
			<link
				key="link-alternate-x-default"
				rel="alternate"
				href={defaultRoute}
				hrefLang="x-default"
			/>

			{langOptions.map(({ value: lang }) => {
				const langPath = stripTrailingSlash(
					`${origin}/${lang}${absolutePath}`
				);

				return (
					<link
						key={`link-alternate-${lang}`}
						rel="alternate"
						href={lang === DEFAULT_LANGUAGE ? defaultRoute : langPath}
						hrefLang={lang}
					/>
				);
			})}
		</>
	);
}

export default SeoTags;
